import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';


export default function CourseCurriculumPage() {
  const { id } = useParams();
  const [sections, setSections] = useState([
    {
      id: 1,
      title: 'Introduction to the Course',
      lessons: [
        { id: 1, title: 'Welcome Video', type: 'video', duration: '5:30', published: true, description: 'Introduction to the course and what you will learn' },
        { id: 2, title: 'Course Overview', type: 'document', duration: '10:00', published: true, description: 'Detailed course syllabus and structure' },
      ],
    },
    {
      id: 2,
      title: 'Getting Started',
      lessons: [
        { id: 3, title: 'Setup Your Environment', type: 'video', duration: '15:20', published: false, description: 'Step-by-step guide to setting up your development environment' },
        { id: 4, title: 'First Assignment', type: 'assignment', duration: '30:00', published: false, description: 'Complete your first coding assignment' },
      ],
    },
  ]);

  const [showAddSection, setShowAddSection] = useState(false);
  const [showAddLesson, setShowAddLesson] = useState(null);
  const [showEditLesson, setShowEditLesson] = useState(null);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newLesson, setNewLesson] = useState({
    title: '',
    type: 'video',
    duration: '',
    description: '',
  });
  const [editLesson, setEditLesson] = useState<Lesson | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleAddSection = () => {
    if (newSectionTitle.trim()) {
      setSections([
        ...sections,
        {
          id: Date.now(),
          title: newSectionTitle,
          lessons: [],
        },
      ]);
      setNewSectionTitle('');
      setShowAddSection(false);
    }
  };

  const handleAddLesson = (sectionId) => {
    if (newLesson.title.trim()) {
      setSections(
        sections.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                lessons: [
                  ...section.lessons,
                  {
                    id: Date.now(),
                    title: newLesson.title,
                    type: newLesson.type,
                    duration: newLesson.duration,
                    published: false,
                    description: newLesson.description,
                    fileName: uploadedFile?.name,
                  },
                ],
              }
            : section
        )
      );
      setNewLesson({ title: '', type: 'video', duration: '', description: '' });
      setUploadedFile(null);
      setShowAddLesson(null);
    }
  };

  const handleEditLesson = () => {
    if (editLesson && showEditLesson) {
      setSections(
        sections.map((section) =>
          section.id === showEditLesson.sectionId
            ? {
                ...section,
                lessons: section.lessons.map((lesson) =>
                  lesson.id === editLesson.id
                    ? {
                        ...editLesson,
                        fileName: uploadedFile?.name || lesson.fileName,
                      }
                    : lesson
                ),
              }
            : section
        )
      );
      setShowEditLesson(null);
      setEditLesson(null);
      setUploadedFile(null);
    }
  };

  const handleDeleteLesson = (sectionId, lessonId) => {
    if (window.confirm('Are you sure you want to delete this lesson?')) {
      setSections(
        sections.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                lessons: section.lessons.filter((lesson) => lesson.id !== lessonId),
              }
            : section
        )
      );
    }
  };

  const handleDeleteSection = (sectionId) => {
    if (window.confirm('Are you sure you want to delete this section and all its lessons?')) {
      setSections(sections.filter((section) => section.id !== sectionId));
    }
  };

  const toggleLessonPublish = (sectionId, lessonId) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              lessons: section.lessons.map((lesson) =>
                lesson.id === lessonId ? { ...lesson, published: !lesson.published } : lesson
              ),
            }
          : section
      )
    );
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video':
        return 'ri-play-circle-line';
      case 'document':
        return 'ri-file-text-line';
      case 'quiz':
        return 'ri-question-line';
      case 'assignment':
        return 'ri-file-edit-line';
      case 'live':
        return 'ri-live-line';
      default:
        return 'ri-file-line';
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const openEditModal = (sectionId, lesson) => {
    setEditLesson({ ...lesson });
    setShowEditLesson({ sectionId, lesson });
    setUploadedFile(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1">
        {/* Page Header */}
        <section className="bg-gray-50 border-b border-gray-200 py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between">
              <div>
                <Link
                  to="/instructor"
                  className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-4 cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  <span>Back to Dashboard</span>
                </Link>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Course Curriculum Builder</h1>
                <p className="text-gray-600">Organize your course content into sections and lessons</p>
              </div>
              <div className="flex items-center gap-3">
                <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                  Preview Course
                </button>
                <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer">
                  Publish Course
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Curriculum Builder */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <div className="space-y-6">
                  {sections.map((section) => (
                    <div key={section.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      {/* Section Header */}
                      <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-b border-gray-200">
                        <div className="flex items-center gap-3">
                          <i className="ri-drag-move-line text-gray-400 cursor-move"></i>
                          <h3 className="font-semibold text-gray-900">{section.title}</h3>
                          <span className="text-sm text-gray-500">
                            {section.lessons.length} {section.lessons.length === 1 ? 'lesson' : 'lessons'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setShowAddLesson(section.id)}
                            className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-add-line text-gray-600"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteSection(section.id)}
                            className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-gray-600 hover:text-red-600"></i>
                          </button>
                        </div>
                      </div>

                      {/* Lessons */}
                      <div className="divide-y divide-gray-100">
                        {section.lessons.map((lesson) => (
                          <div key={lesson.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4 flex-1">
                                <i className="ri-drag-move-line text-gray-400 cursor-move"></i>
                                <i className={`${getLessonIcon(lesson.type)} text-purple-600 text-xl w-6 h-6 flex items-center justify-center`}></i>
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-1">
                                    <h4 className="font-medium text-gray-900">{lesson.title}</h4>
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded-full ${
                                        lesson.published
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-gray-100 text-gray-600'
                                      }`}
                                    >
                                      {lesson.published ? 'Published' : 'Draft'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 text-sm text-gray-500">
                                    <span className="capitalize">{lesson.type}</span>
                                    <span>•</span>
                                    <span>{lesson.duration}</span>
                                    {lesson.fileName && (
                                      <>
                                        <span>•</span>
                                        <span className="flex items-center gap-1">
                                          <i className="ri-attachment-line"></i>
                                          {lesson.fileName}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleLessonPublish(section.id, lesson.id)}
                                  className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                >
                                  <i className={`${lesson.published ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-600`}></i>
                                </button>
                                <button
                                  onClick={() => openEditModal(section.id, lesson)}
                                  className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                >
                                  <i className="ri-edit-line text-gray-600"></i>
                                </button>
                                <button
                                  onClick={() => handleDeleteLesson(section.id, lesson.id)}
                                  className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line text-gray-600 hover:text-red-600"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Add Lesson Form */}
                        {showAddLesson === section.id && (
                          <div className="px-6 py-4 bg-purple-50 border-t-2 border-purple-200">
                            <h4 className="font-medium text-gray-900 mb-4">Add New Lesson</h4>
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Lesson Title
                                </label>
                                <input
                                  type="text"
                                  value={newLesson.title}
                                  onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
                                  placeholder="Enter lesson title"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Lesson Type
                                  </label>
                                  <select
                                    value={newLesson.type}
                                    onChange={(e) =>
                                      setNewLesson({ ...newLesson, type: e.target.value })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm pr-8"
                                  >
                                    <option value="video">Video</option>
                                    <option value="document">Document</option>
                                    <option value="quiz">Quiz</option>
                                    <option value="assignment">Assignment</option>
                                    <option value="live">Live Session</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Duration
                                  </label>
                                  <input
                                    type="text"
                                    value={newLesson.duration}
                                    onChange={(e) => setNewLesson({ ...newLesson, duration: e.target.value })}
                                    placeholder="e.g., 15:30"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Description (Optional)
                                </label>
                                <textarea
                                  value={newLesson.description}
                                  onChange={(e) => setNewLesson({ ...newLesson, description: e.target.value })}
                                  placeholder="Enter lesson description"
                                  rows={2}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm resize-none"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Upload File (Optional)
                                </label>
                                <input
                                  type="file"
                                  onChange={handleFileUpload}
                                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 file:cursor-pointer"
                                />
                                {uploadedFile && (
                                  <p className="text-sm text-gray-600 mt-1">Selected: {uploadedFile.name}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 pt-2">
                                <button
                                  onClick={() => handleAddLesson(section.id)}
                                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                                >
                                  Add Lesson
                                </button>
                                <button
                                  onClick={() => {
                                    setShowAddLesson(null);
                                    setNewLesson({ title: '', type: 'video', duration: '', description: '' });
                                    setUploadedFile(null);
                                  }}
                                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm whitespace-nowrap cursor-pointer"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Add Section Button */}
                  {!showAddSection ? (
                    <button
                      onClick={() => setShowAddSection(true)}
                      className="w-full px-6 py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-purple-400 hover:text-purple-600 transition-colors flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <i className="ri-add-line text-xl"></i>
                      <span className="font-medium">Add New Section</span>
                    </button>
                  ) : (
                    <div className="bg-white border-2 border-purple-400 rounded-xl p-6">
                      <h3 className="font-semibold text-gray-900 mb-4">Add New Section</h3>
                      <input
                        type="text"
                        value={newSectionTitle}
                        onChange={(e) => setNewSectionTitle(e.target.value)}
                        placeholder="Enter section title"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4 text-sm"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleAddSection}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Add Section
                        </button>
                        <button
                          onClick={() => {
                            setShowAddSection(false);
                            setNewSectionTitle('');
                          }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-white border border-gray-200 rounded-xl p-6 sticky top-24">
                  <h3 className="font-semibold text-gray-900 mb-4">Course Statistics</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Total Sections</span>
                      <span className="font-semibold text-gray-900">{sections.length}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Total Lessons</span>
                      <span className="font-semibold text-gray-900">
                        {sections.reduce((acc, section) => acc + section.lessons.length, 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Published Lessons</span>
                      <span className="font-semibold text-green-600">
                        {sections.reduce(
                          (acc, section) => acc + section.lessons.filter((l) => l.published).length,
                          0
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-3">
                      <span className="text-sm text-gray-600">Draft Lessons</span>
                      <span className="font-semibold text-gray-500">
                        {sections.reduce(
                          (acc, section) => acc + section.lessons.filter((l) => !l.published).length,
                          0
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">Quick Actions</h4>
                    <div className="space-y-2">
                      <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2 cursor-pointer">
                        <i className="ri-upload-line text-purple-600"></i>
                        <span>Upload Content</span>
                      </button>
                      <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2 cursor-pointer">
                        <i className="ri-file-copy-line text-purple-600"></i>
                        <span>Import from Template</span>
                      </button>
                      <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2 cursor-pointer">
                        <i className="ri-download-line text-purple-600"></i>
                        <span>Export Curriculum</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Edit Lesson Modal */}
      {showEditLesson && editLesson && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Edit Lesson</h3>
              <button
                onClick={() => {
                  setShowEditLesson(null);
                  setEditLesson(null);
                  setUploadedFile(null);
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lesson Title
                </label>
                <input
                  type="text"
                  value={editLesson.title}
                  onChange={(e) => setEditLesson({ ...editLesson, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lesson Type
                  </label>
                  <select
                    value={editLesson.type}
                    onChange={(e) =>
                      setEditLesson({ ...editLesson, type: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm pr-8"
                  >
                    <option value="video">Video</option>
                    <option value="document">Document</option>
                    <option value="quiz">Quiz</option>
                    <option value="assignment">Assignment</option>
                    <option value="live">Live Session</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration
                  </label>
                  <input
                    type="text"
                    value={editLesson.duration}
                    onChange={(e) => setEditLesson({ ...editLesson, duration: e.target.value })}
                    placeholder="e.g., 15:30"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={editLesson.description || ''}
                  onChange={(e) => setEditLesson({ ...editLesson, description: e.target.value })}
                  placeholder="Enter lesson description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content File
                </label>
                {editLesson.fileName && !uploadedFile && (
                  <div className="mb-2 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-file-line text-purple-600"></i>
                      <span className="text-sm text-gray-700">{editLesson.fileName}</span>
                    </div>
                    <span className="text-xs text-gray-500">Current file</span>
                  </div>
                )}
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 file:cursor-pointer"
                />
                {uploadedFile && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <i className="ri-check-line"></i>
                    New file selected: {uploadedFile.name}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">Publish Status</p>
                  <p className="text-xs text-gray-500">Make this lesson visible to students</p>
                </div>
                <button
                  onClick={() => setEditLesson({ ...editLesson, published: !editLesson.published })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                    editLesson.published ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      editLesson.published ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={handleEditLesson}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setShowEditLesson(null);
                  setEditLesson(null);
                  setUploadedFile(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}